import { useEffect } from "react";

/**
 * Lightweight SEO component — sets document title, meta tags, canonical and
 * JSON-LD structured data. Avoids needing react-helmet to keep deps small.
 */
export default function SEO({
  title,
  description,
  path = "/",
  image,
  type = "website",
  jsonLd,
  noIndex = false,
}) {
  useEffect(() => {
    if (title) document.title = title;

    const ensure = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(attrs.tag);
        Object.entries(attrs.attrs || {}).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      return el;
    };

    if (description) {
      const meta = ensure('meta[name="description"]', { tag: "meta", attrs: { name: "description" } });
      meta.setAttribute("content", description);
    }

    // Open Graph
    const setOg = (property, content) => {
      if (!content) return;
      const el = ensure(`meta[property="${property}"]`, { tag: "meta", attrs: { property } });
      el.setAttribute("content", content);
    };
    setOg("og:title", title);
    setOg("og:description", description);
    setOg("og:type", type);
    setOg("og:image", image);
    setOg("og:site_name", "Tech Emergency");
    // og:url must match the canonical so social previews link back to the right page.
    // We deliberately use the production origin (not the dev preview origin) so OG
    // scrapers always land on the live URL, even if the page was shared from preview.
    setOg("og:url", `https://techemergency.com.au${path}`);

    // Twitter
    const setTw = (name, content) => {
      if (!content) return;
      const el = ensure(`meta[name="${name}"]`, { tag: "meta", attrs: { name } });
      el.setAttribute("content", content);
    };
    setTw("twitter:card", "summary_large_image");
    setTw("twitter:title", title);
    setTw("twitter:description", description);
    setTw("twitter:image", image);

    // Canonical — always point to the production domain. The preview environment
    // sets its own origin (e.g. *.preview.emergentagent.com), but we never want
    // Google to index preview URLs.
    const canonical = ensure('link[rel="canonical"]', { tag: "link", attrs: { rel: "canonical" } });
    canonical.setAttribute("href", `https://techemergency.com.au${path}`);

    // Robots — set noindex/nofollow for pages we don't want Google to surface
    // (404, internal flyers, admin, etc.)
    const robots = ensure('meta[name="robots"]', { tag: "meta", attrs: { name: "robots" } });
    robots.setAttribute(
      "content",
      noIndex ? "noindex, nofollow, noarchive" : "index, follow"
    );

    // JSON-LD — supports a single object OR an array of objects.
    const baseId = "te-jsonld";
    // Remove any previously-injected blocks (single or multi).
    Array.from(
      document.querySelectorAll(`script[data-te-jsonld="1"], #${baseId}`)
    ).forEach((el) => el.remove());

    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((block, i) => {
        if (!block) return;
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-te-jsonld", "1");
        if (i === 0) script.id = baseId;
        script.textContent = JSON.stringify(block);
        document.head.appendChild(script);
      });
    }
  }, [title, description, path, image, type, jsonLd, noIndex]);

  return null;
}

export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://www.techemergency.com.au/#business",
  name: "Tech Emergency",
  description:
    "Adelaide residential computer repairs and home IT support — mobile visits to your home. Computer and laptop repairs, Wi-Fi troubleshooting, printers, email help, virus removal and patient tech support for seniors.",
  telephone: "+611800836390",
  url: "https://www.techemergency.com.au",
  priceRange: "$$",
  areaServed: {
    "@type": "City",
    name: "Adelaide",
    address: { "@type": "PostalAddress", addressRegion: "SA", addressCountry: "AU" },
  },
  address: {
    "@type": "PostalAddress",
    addressLocality: "Adelaide",
    addressRegion: "SA",
    addressCountry: "AU",
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "09:00",
      closes: "19:00",
    },
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    reviewCount: "61",
  },
  sameAs: [
    "https://www.facebook.com/profile.php?id=61570216282145",
    "https://www.instagram.com/techemergency",
  ],
};
